/**
 * Theme: persisted choice.
 *
 * The bootstrap that applies the stored theme *before first paint* lives in
 * `index.html` as a plain inline script — deliberately not a React component,
 * because a `<script>` rendered by React never executes on the client and React
 * warns about it. This module owns everything after mount: reading the current
 * value and switching.
 *
 * `data-theme` is the single source of truth that both CSS and the WebGL board
 * read, so there is no second copy of the theme in JS state.
 */

import { useEffect, useState } from "react";

export const THEME_KEY = "kc-theme";
export type Theme = "dark" | "light";

/** Runs before paint. Kept tiny and dependency-free on purpose. */
export const THEME_BOOTSTRAP = `
(function () {
  try {
    var stored = localStorage.getItem("${THEME_KEY}");
    var theme = stored === "light" || stored === "dark"
      ? stored
      : (window.matchMedia("(prefers-color-scheme: light)").matches ? "light" : "dark");
    document.documentElement.setAttribute("data-theme", theme);
  } catch (error) {
    document.documentElement.setAttribute("data-theme", "dark");
  }
})();
`.trim();

export function useTheme(): [Theme, (next: Theme) => void] {
  const [theme, setTheme] = useState<Theme>("dark");

  useEffect(() => {
    const current = document.documentElement.getAttribute("data-theme");
    if (current === "light" || current === "dark") setTheme(current);
  }, []);

  const apply = (next: Theme) => {
    document.documentElement.setAttribute("data-theme", next);
    try {
      localStorage.setItem(THEME_KEY, next);
    } catch {
      // Private mode: the theme still applies for this session.
    }
    setTheme(next);
  };

  return [theme, apply];
}

/**
 * The switch itself. One control, both surfaces.
 *
 * The label names the theme you would switch TO, matching the aria-label —
 * labelling it with the current state made the control read as a status rather
 * than an action. The text is dropped below `sm`, where four Chinese characters
 * next to a sign-out button does not fit, and the switch graphic carries it.
 */
export function ThemeToggle({ className = "" }: { className?: string }) {
  const [theme, setTheme] = useTheme();
  const next: Theme = theme === "dark" ? "light" : "dark";
  const target = next === "dark" ? "深色" : "浅色";
  return (
    <button
      type="button"
      onClick={() => setTheme(next)}
      className={`silk-label inline-flex shrink-0 items-center gap-2 whitespace-nowrap border border-line-strong px-3 py-1.5 text-[10px] text-silk-dim transition-colors hover:border-silk hover:text-silk ${className}`}
      aria-label={`切换到${target}主题`}
      title={`切换到${target}主题`}
    >
      <span aria-hidden className="relative block h-3 w-6 shrink-0 border border-current">
        <span
          className="absolute top-0.5 h-1.5 w-2.5 bg-signal transition-[left] duration-200"
          style={{ left: theme === "dark" ? "2px" : "12px" }}
        />
      </span>
      <span className="hidden sm:inline">{target}</span>
    </button>
  );
}
